/**
 * LogLevel is used to control what level of messages are printed.
 */

/**
 * An object to print standardized log messages to the console.
 */

const levels = ['debug', 'info', 'warn', 'error', 'none'];
const defaultLevel = levels.indexOf('error');
const maxLevel = levels.length - 1;
const toLogLevel = level => {
  const logLevel = levels.indexOf(level);
  if (logLevel > -1) {
    return logLevel;
  }
  return defaultLevel;
};

// Formats to account for
// ?nova-log=debug - Set default level
// ?nova-log=Avatar:warn - Set level of Avatar logger
// ?nova-log=Avatar:info,i18n:error,warn - Set Avatar to info, i18n to error, and set default to warn
const configuredLevels = new URLSearchParams(window.location.search).getAll('nova-log').reduce((configuredLevels, logConfig) => {
  const configs = logConfig.split(',').filter(c => !!c);
  for (const c of configs) {
    if (c.includes(':')) {
      const parts = c.split(':');
      configuredLevels[parts[0]] = toLogLevel(parts[1]);
    } else {
      configuredLevels[''] = toLogLevel(c);
    }
  }
  return configuredLevels;
}, {
  '': defaultLevel
});
const getLogLevelRank = (name = '') => {
  const level = configuredLevels[name] ?? maxLevel;
  const defaultLevel = configuredLevels[''];
  // A more verbose default level can override less verbose
  return Math.min(level, defaultLevel);
};

/**
 * Set the log level for a named Logger if the name is provided. If no name is provided sets the global log level.
 * When settings the level, all messages for that level and above are printed. Message levels from lowest to highest
 * are "debug", "info", "warn", and "error". For example, setting the log level to "info" will allow "info", "warn",
 * and "error" messages to be printed but not "debug".
 *
 * The default log level is "error".
 *
 * If a log level is set for a named Logger, that will take precedence over the global log level.
 *
 * If you need to set a log level at runtime for debugging, you can access this function globally in developer console
 * via `sas.nova.logging.setLogLevel`.
 *
 * Log levels can also be initialized with query parameters. The main use case for this is when something is logging
 * something below the default level before you get a chance to set the level manually in the developer console. Use
 * query parameter "nova-log" to do this. See the examples below for specific usage.
 *
 * ?nova-log=debug - Set global level to "debug"
 * ?nova-log=Avatar:warn - Set level of Avatar logger to "warn"
 * ?nova-log=Avatar:info,i18n:error,warn - Set Avatar to "info", i18n to "error", and set default to "warn"
 *
 * @param level the level to set the named Logger to
 * @param name the name of the logger, if not provided sets the global log level
 */
export const setLogLevel = (level, name = '') => {
  configuredLevels[name] = levels.indexOf(level);
};

/**
 * @param name name of a Logger to get the log level, if not provided return value will be for the global log level
 * @returns the log level of the specified Logger, if no name is provided, returns the global log level
 */
export const getLogLevel = (name = '') => levels[getLogLevelRank(name)];

// Intl.DateTimeFormat is not supported for displaying dates to end user but we're printing logs.
// The options must be cast to any because TypeScript flags the fractionalSecondDigits as an error.
// The property does exist according to MDN: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
const dateFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  fractionalSecondDigits: 3
});
const formatMessage = (message, name, level) => {
  return `${dateFormatter.format(Date.now())} ${level.toUpperCase()} ${name ? `${name}: ` : ''}${message}`;
};
const log = (rank, name, message) => {
  const configuredLevel = getLogLevelRank(name);
  if (rank >= configuredLevel) {
    const level = levels[rank];
    const messageText = (() => {
      switch (typeof message) {
        case 'function':
          return message();
        case 'object':
          return JSON.stringify(message);
        default:
          return message;
      }
    })();
    if (level === 'none') return;
    // eslint-disable-next-line no-console
    console[level](formatMessage(messageText, name, level));
  }
};

/**
 * Returns a `Logger` with an optional name. If a `name` is provided that name will be included in the log output. This
 * can be used to distinguish the log output from one component vs another. Most browser developer tools offer a way to
 * filter console messasges. The name is also used with `setLogLevel` to control which levels of log are actually
 * printed.
 *
 * @param name name of the logger
 * @returns A logger associated with the passed name
 */
export const getLogger = (name = '') => {
  return levels.reduce((logger, level, rank) => {
    if (level !== 'none') {
      logger[level] = m => log(rank, name, m);
    }
    return logger;
  }, {});
};

// Make the API accessible via window so that we can use it from the console during debugging.
(function (scope) {
  scope.sas = scope.sas || {};
  if (Object.isFrozen(scope.sas)) {
    // If nova loads with OpenUI there is a chance that the global 'sas' will be frozen. We should
    // prevent attempts to modify it since that will just throw an exception.
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    getLogger('Logging').warn('Could not create globally accessible nova logger');
    return;
  }
  scope.sas.nova = scope.sas.nova || {};
  scope.sas.nova.logging = {
    getLogger,
    getLogLevel,
    setLogLevel
  };
})(window);
//# sourceMappingURL=index.js.map